/**
 * Group stage component with drag-to-reorder interface.
 *
 * UX: Users rank countries by dragging them into order.
 * Tap a card to view details, drag to reorder.
 *
 * Desktop: Split-panel layout with inline details and drag-to-reorder.
 * Mobile: Stacked cards with drag handles and swipe navigation between groups.
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import type { CountryCode } from '@/data/countries/types';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  clearDraftRatings,
  loadDraftRatings,
  saveDraftRatings,
} from '@/lib/tournament';
import type { GroupCountryRating } from '@/lib/tournament/types';
import { cn } from '@/lib/utils';
import { GroupCard } from './GroupCard';
import { GroupStageDesktop } from './GroupStageDesktop';
import { useTournament } from './TournamentProvider';

interface RatedCountry {
  code: CountryCode;
  stars: number | null;
}

// Top 2 countries in each group advance to bracket stage
const QUALIFIERS_PER_GROUP = 2;

/**
 * Sortable wrapper for mobile GroupCard
 */
function SortableMobileCard({
  country,
  index,
  disabled,
}: {
  country: RatedCountry;
  index: number;
  disabled?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: country.code, disabled });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition: transition ?? 'transform 200ms ease',
    zIndex: isDragging ? 50 : 0,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex-1 min-h-0 flex',
        isDragging && 'opacity-80',
      )}
    >
      <GroupCard
        code={country.code}
        index={index}
        qualifies={index < QUALIFIERS_PER_GROUP}
        disabled={disabled || isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

export function GroupStage() {
  const { currentGroup, progress, actions, state } = useTournament();

  // Local state: array of { code, stars } in display order
  const [ratedCountries, setRatedCountries] = useState<RatedCountry[]>([]);

  // Navigation helpers
  const currentGroupIndex = state ? state.currentGroupIndex : 0;
  const totalGroups = state ? state.groups.length : 0;
  const canGoPrev = currentGroupIndex > 0;
  const canGoNext = currentGroupIndex < totalGroups - 1;

  const handlePrevGroup = useCallback(() => {
    if (canGoPrev) {
      actions.goToGroup(currentGroupIndex - 1);
    }
  }, [canGoPrev, currentGroupIndex, actions]);

  const handleNextGroup = useCallback(() => {
    if (canGoNext) {
      actions.goToGroup(currentGroupIndex + 1);
    }
  }, [canGoNext, currentGroupIndex, actions]);

  // Get scopeId for draft persistence
  const scopeId = state?.scopeId ?? '';

  // Initialize ratings when group changes
  useEffect(() => {
    if (!currentGroup || !scopeId) return;

    // Check if group already has submitted ratings (completed group)
    if (currentGroup.ratings && currentGroup.ratings.length > 0) {
      // Use submitted ratings (user is reviewing a completed group)
      const sorted = [...currentGroup.ratings].sort((a, b) => {
        if (b.stars !== a.stars) return b.stars - a.stars;
        return a.tiebreakPosition - b.tiebreakPosition;
      });
      setRatedCountries(sorted.map((r) => ({ code: r.code, stars: r.stars })));
      return;
    }

    // Try to restore draft ratings
    const draft = loadDraftRatings(scopeId, currentGroup.id);
    if (draft && draft.length === currentGroup.countries.length) {
      // Validate draft has same countries
      const draftCodes = new Set(draft.map((d) => d.code));
      const groupCodes = new Set(currentGroup.countries);
      const isValid = currentGroup.countries.every((c) => draftCodes.has(c));

      if (isValid) {
        // Restore draft (already sorted)
        setRatedCountries(draft);
        return;
      }
    }

    // Initialize fresh with no ratings
    setRatedCountries(
      currentGroup.countries.map((code) => ({ code, stars: null })),
    );
  }, [currentGroup?.id, scopeId]);

  // Mobile dnd-kit sensors with touch support
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleMobileDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = ratedCountries.findIndex((c) => c.code === active.id);
        const newIndex = ratedCountries.findIndex((c) => c.code === over.id);
        const newOrder = arrayMove(ratedCountries, oldIndex, newIndex);
        setRatedCountries(newOrder);
        // Persist draft immediately
        if (currentGroup && scopeId) {
          saveDraftRatings(scopeId, currentGroup.id, newOrder);
        }
      }
    },
    [ratedCountries, currentGroup, scopeId],
  );

  // Convert to format expected by TournamentProvider
  const handleSubmit = useCallback(() => {
    if (ratedCountries.length === 0 || !currentGroup || !scopeId) return;

    // Convert to GroupCountryRating[] format
    // Unrated countries get 1 star (lowest rating)
    const ratings: GroupCountryRating[] = ratedCountries.map((item, index) => ({
      code: item.code,
      stars: item.stars ?? 1, // Unrated defaults to 1
      tiebreakPosition: index + 1,
    }));

    // Clear draft after successful submission
    clearDraftRatings(scopeId, currentGroup.id);

    actions.submitGroupRating(ratings);
  }, [ratedCountries, actions, currentGroup, scopeId]);

  // Desktop detection for split-panel layout
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  // Handle manual reordering (desktop drag-and-drop)
  const handleReorder = useCallback(
    (newOrder: RatedCountry[]) => {
      setRatedCountries(newOrder);
      // Persist draft immediately
      if (currentGroup && scopeId) {
        saveDraftRatings(scopeId, currentGroup.id, newOrder);
      }
    },
    [currentGroup, scopeId],
  );

  if (!currentGroup) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">No group to rate</p>
      </div>
    );
  }

  // Desktop layout: split-panel with inline details
  if (isDesktop) {
    return (
      <div className="h-full flex flex-col">
        {/* Header with navigation */}
        <div className="shrink-0 mb-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrevGroup}
              disabled={!canGoPrev}
              className="p-2 -ml-2 rounded-full hover:bg-muted/50 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
              aria-label="Previous group"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <span className="text-sm text-muted-foreground">
              {progress.currentGroup} / {progress.totalGroups}
            </span>
            <button
              onClick={handleNextGroup}
              disabled={!canGoNext}
              className="p-2 -mr-2 rounded-full hover:bg-muted/50 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
              aria-label="Next group"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Desktop split-panel layout */}
        <div className="flex-1 min-h-0">
          <GroupStageDesktop
            ratedCountries={ratedCountries}
            onReorder={handleReorder}
          />
        </div>

        {/* Submit button */}
        <div className="shrink-0 pt-4">
          <Button
            onClick={handleSubmit}
            className="w-full"
          >
            <Check className="w-4 h-4 mr-2" />
            Confirm & Continue
          </Button>
        </div>
      </div>
    );
  }

  // Mobile layout: stacked cards with drag-to-reorder
  return (
    <div className="h-full flex flex-col">
      {/* Header with navigation */}
      <div className="shrink-0 mb-2">
        <div className="flex items-center justify-between">
          {/* Previous button */}
          <button
            onClick={handlePrevGroup}
            disabled={!canGoPrev}
            className="p-2 -ml-2 rounded-full hover:bg-muted/50 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity touch-manipulation"
            aria-label="Previous group"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Group counter */}
          <span className="text-sm text-muted-foreground">
            {progress.currentGroup} / {progress.totalGroups}
          </span>

          {/* Next button */}
          <button
            onClick={handleNextGroup}
            disabled={!canGoNext}
            className="p-2 -mr-2 rounded-full hover:bg-muted/50 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity touch-manipulation"
            aria-label="Next group"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Country cards with drag-to-reorder */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleMobileDragEnd}
      >
        <SortableContext
          items={ratedCountries.map((c) => c.code)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex-1 min-h-0 flex flex-col gap-2">
            {ratedCountries.map((rated, index) => (
              <SortableMobileCard
                key={rated.code}
                country={rated}
                index={index}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Submit button - confirms order and moves to next */}
      <div className="shrink-0 pt-2">
        <Button
          onClick={handleSubmit}
          className="w-full"
        >
          <Check className="w-4 h-4 mr-2" />
          Confirm & Continue
        </Button>
      </div>
    </div>
  );
}
